import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_SUBJECT = 'Activate your Chimba Travel account';

const SiteSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [text, setText] = useState('');
  const [defaultLeadEmail, setDefaultLeadEmail] = useState('');
  const [subjectLines, setSubjectLines] = useState([DEFAULT_SUBJECT, '', '']);
  const [stats, setStats] = useState([]);

  useEffect(() => {
    axios.get(`${API}/site-settings`)
      .then((res) => {
        setEnabled(Boolean(res.data?.under_construction_banner));
        setText(res.data?.banner_text || '');
        setDefaultLeadEmail(res.data?.default_lead_email || '');
        const lines = res.data?.invite_subject_lines || [DEFAULT_SUBJECT];
        setSubjectLines([lines[0] || DEFAULT_SUBJECT, lines[1] || '', lines[2] || '']);
      })
      .catch(() => toast.error('Failed to load site settings'))
      .finally(() => setLoading(false));

    const tok = localStorage.getItem('token');
    if (tok) {
      axios.get(`${API}/admin/invite-stats`, { headers: { Authorization: `Bearer ${tok}` } })
        .then((res) => setStats(res.data || []))
        .catch(() => { /* silent */ });
    }
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/site-settings`,
        {
          under_construction_banner: enabled,
          banner_text: text,
          default_lead_email: defaultLeadEmail || null,
          invite_subject_lines: subjectLines.map((s) => s.trim()).filter(Boolean),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Site settings saved');
    } catch {
      toast.error('Failed to save site settings');
    } finally {
      setSaving(false);
    }
  };

  const setLine = (idx, val) => setSubjectLines((arr) => arr.map((s, i) => (i === idx ? val : s)));

  return (
    <div className="card mt-8" data-testid="site-settings-card">
      <div className="flex items-center gap-3 mb-4">
        <SettingsIcon className="text-terracotta" size={24} />
        <h3 className="text-xl font-heading font-bold text-maroon">Site Settings</h3>
      </div>

      {loading ? (
        <p className="text-maroon/60">Loading…</p>
      ) : (
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="mt-1 w-5 h-5 accent-terracotta"
              data-testid="under-construction-toggle"
            />
            <div>
              <p className="font-semibold text-maroon">Show "Under Construction" banner</p>
              <p className="text-sm text-maroon/60">
                Toggle off when you're ready for public launch. Changes apply immediately on next page load.
              </p>
            </div>
          </label>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-maroon">Banner text</label>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full px-3 py-2 border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
              placeholder="Site Under Construction - We're adding final touches!"
              data-testid="banner-text-input"
            />
          </div>

          <div className="space-y-2 pt-4 border-t border-maroon/10">
            <label className="text-sm font-semibold text-maroon">Default lead email</label>
            <input
              type="email"
              value={defaultLeadEmail}
              onChange={(e) => setDefaultLeadEmail(e.target.value)}
              className="w-full px-3 py-2 border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
              placeholder="leads@chimbatravel.com"
              data-testid="default-lead-email-input"
            />
            <p className="text-xs text-maroon/50">
              Where quote-request leads are sent when a Partner Package doesn't have its own
              <code className="bg-sand/50 px-1 mx-1 rounded">supplier_email</code>. Leave blank to use the default site notification address.
            </p>
          </div>

          <div className="space-y-2 pt-4 border-t border-maroon/10">
            <label className="text-sm font-semibold text-maroon">Member-Invite email subject lines (A/B test)</label>
            <p className="text-xs text-maroon/50">
              The backend picks one at random per invite. Use <code className="bg-sand/50 px-1 rounded">{'{first_name}'}</code> and <code className="bg-sand/50 px-1 rounded">{'{package_title}'}</code> as placeholders. Leave blank slots empty.
            </p>
            {[0, 1, 2].map((i) => (
              <input
                key={i}
                type="text"
                value={subjectLines[i] || ''}
                onChange={(e) => setLine(i, e.target.value)}
                placeholder={i === 0 ? 'e.g., {first_name} — your trip awaits' : '(optional)'}
                className="w-full px-3 py-2 border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
                data-testid={`invite-subject-line-${i}`}
              />
            ))}

            {stats.length > 0 && (
              <div className="mt-3 p-3 bg-sand/30 border border-maroon/10 rounded-sm" data-testid="invite-stats-panel">
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-maroon">
                  <BarChart3 size={14} className="text-terracotta" />
                  Last 90 days · A/B performance
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-maroon/60">
                      <th className="pr-3 font-semibold">Subject</th>
                      <th className="px-2 font-semibold text-right">Sent</th>
                      <th className="px-2 font-semibold text-right">Converted</th>
                      <th className="pl-2 font-semibold text-right">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((row, idx) => (
                      <tr key={idx} className="border-t border-maroon/10">
                        <td className="py-1.5 pr-3 text-maroon truncate max-w-[280px]" title={row.subject}>{row.subject}</td>
                        <td className="px-2 text-right text-maroon/80">{row.sent}</td>
                        <td className="px-2 text-right text-maroon/80">{row.converted}</td>
                        <td className="pl-2 text-right font-bold text-terracotta">{row.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm disabled:opacity-50"
            data-testid="save-site-settings-btn"
          >
            <Save size={16} />
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SiteSettingsCard;
